import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Download, Trash2, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const HistoryPage = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingId, setExportingId] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/history`);
      setHistory(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement de l'historique");
    } finally {
      setLoading(false);
    }
  };

  const deleteHistoryItem = async (id) => {
    try {
      await axios.delete(`${API}/history/${id}`);
      setHistory(history.filter(item => item.id !== id));
      toast.success("Élément supprimé de l'historique");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const clearAllHistory = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir effacer tout l'historique ?")) {
      return;
    }
    try {
      await axios.delete(`${API}/history`);
      setHistory([]);
      toast.success("Historique effacé");
    } catch (error) {
      toast.error("Erreur lors de l'effacement");
    }
  };

  const exportToExcel = async (historyId) => {
    setExportingId(historyId);
    try {
      const response = await axios.get(`${API}/export/excel/${historyId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `search_results_${historyId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export Excel réussi");
    } catch (error) {
      toast.error("Erreur lors de l'export Excel");
    } finally {
      setExportingId(null);
    }
  };

  const exportToPDF = async (historyId) => {
    setExportingId(historyId);
    try {
      const response = await axios.get(`${API}/export/pdf/${historyId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `search_results_${historyId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export PDF réussi");
    } catch (error) {
      toast.error("Erreur lors de l'export PDF");
    } finally {
      setExportingId(null);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleBack = () => {
    // Si la page a été ouverte dans un nouvel onglet (pas d'historique), fermer l'onglet
    if (window.history.length <= 2) {
      window.close();
      // Si window.close() ne fonctionne pas (restrictions navigateur), naviguer vers /search
      navigate("/search");
    } else {
      // Sinon, revenir à la page précédente dans l'historique
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto fade-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-search-btn"
              onClick={handleBack}
              variant="outline"
              className="bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800 hover:border-gray-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-5xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Historique
              </h1>
              <p className="text-lg text-gray-400 mt-2">
                {history.length} recherche(s) enregistrée(s)
              </p>
            </div>
          </div>
          {history.length > 0 && (
            <Button
              data-testid="clear-all-history-btn"
              onClick={clearAllHistory}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Tout effacer
            </Button>
          )}
        </div>

        {/* History List */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : history.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center">
            <p className="text-xl text-gray-400">Aucune recherche dans l'historique</p>
          </div>
        ) : (
          <div className="space-y-4">
            {history.map((item) => (
              <div
                key={item.id}
                data-testid={`history-item-${item.id}`}
                className="glass rounded-2xl p-6 slide-in card-hover"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2">"{item.query}"</h3>
                    <p className="text-sm text-gray-400">{formatDate(item.timestamp)}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid={`export-excel-${item.id}`}
                      onClick={() => exportToExcel(item.id)}
                      disabled={exportingId === item.id}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {exportingId === item.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <FileSpreadsheet className="w-4 h-4 mr-2" />
                          Excel
                        </>
                      )}
                    </Button>
                    <Button
                      data-testid={`export-pdf-${item.id}`}
                      onClick={() => exportToPDF(item.id)}
                      disabled={exportingId === item.id}
                      size="sm"
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {exportingId === item.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <FileText className="w-4 h-4 mr-2" />
                          PDF
                        </>
                      )}
                    </Button>
                    <Button
                      data-testid={`delete-history-${item.id}`}
                      onClick={() => deleteHistoryItem(item.id)}
                      size="sm"
                      variant="destructive"
                      className="bg-red-600/20 hover:bg-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {item.results.map((result, index) => (
                    <div
                      key={index}
                      data-testid={`history-result-${item.id}-${result.site_id}`}
                      className="p-3 rounded-lg bg-gray-900/30 border border-gray-800"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{result.site_name}</span>
                        <span className={`text-xs px-2 py-1 rounded ${result.status === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                          {result.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryPage;